/* ==========================================================================
   nma-consentimiento.js — bloqueo previo de scripts de terceros (R2)

   QUÉ RESUELVE
   La Ley 21.719 pide que nada no esencial se cargue antes de que el visitante
   consienta. El caso que lo motivó fue el widget de GoHighLevel, que disparaba
   15 solicitudes y dejaba cuatro identificadores persistentes en localStorage
   apenas se abría la portada, sin que nadie hubiera aceptado nada.

   🟠 HOY NO ESTÁ CUSTODIANDO NADA, Y NO ES UN DESCUIDO.
   GoHighLevel se eliminó del tema el 01-09-2026 (CUSTOM.md §40) y era el único
   `[data-nma-consentimiento]` que quedaba. El archivo se conserva porque es el
   MECANISMO, no el widget: sin él, el próximo script de terceros que alguien
   pegue en un `custom-liquid` vuelve a cargar sin consentimiento y el
   incumplimiento reaparece en silencio. Cuesta 120 líneas y no corre nada
   mientras no haya un nodo marcado.

   Para volver a usarlo no hay que tocar este archivo: se marca el script con
   `type="text/plain"` y `data-nma-consentimiento`, como se explica abajo.

   POR QUÉ NO HAY BANNER PROPIO
   Porque no hace falta. El banner nativo de Shopify cumple R1: modo opt-in,
   cuatro categorías separadas, y Aceptar y Rechazar con estilos computados
   idénticos (verificado, no mirado). Construir uno propio sería reemplazar
   algo que ya cumple y además obligaría a mantener el nuestro al día con la
   regulación. Acá solo se agrega lo que Shopify NO hace.

   QUÉ HACE Y QUÉ NO HACE `Shopify.customerPrivacy`
   Reporta el estado del consentimiento y avisa cuando cambia. **No bloquea
   nada de terceros** — eso lo aplica solo a sus propios píxeles. Así que este
   archivo la usa como FUENTE DE VERDAD y pone el bloqueo, sin inventar
   almacenamiento propio ni una segunda noción de "consentimiento".

   CÓMO SE MARCA UN SCRIPT PARA QUE ESPERE

     <script type="text/plain" data-nma-consentimiento="marketing"
             src="https://…"></script>

   `type="text/plain"` hace que el navegador no lo ejecute ni lo descargue.
   Cuando la categoría queda consentida, el script se vuelve a crear como
   JavaScript y corre. Sin recargar la página, que es lo que pide R2.

   ⚠️ NO SE PUEDE CAMBIAR EL `type` Y YA: un <script> ya insertado no se
   ejecuta por mutarle el atributo. Hay que crear un nodo nuevo y reemplazar.

   ALCANCE — LO QUE ESTE ARCHIVO NO PUEDE HACER
   Solo alcanza a lo que vive en el tema. Los app embeds los inyecta Shopify
   antes de que corra nada nuestro y no cuelgan de ninguna sección:

     · Judge.me ................ incumplimiento residual, documentado
     · MAG ..................... sale con la migración de reseñas
     · Mercado Pago Antifraud .. NO se bloquea a propósito: prevención de
                                 fraude en pagos califica como estrictamente
                                 necesaria y no requiere opt-in. Va declarado
                                 en esa categoría en el inventario de R12.
     · musbu-wa-chat ........... ya deshabilitado, cero solicitudes

   Y ya NO están en la lista, porque salieron del tema:

     · GoHighLevel ............. eliminado el 01-09-2026 (§40). Era el único
                                 nodo marcado; el bloqueo queda sin clientes

   Ver CUSTOM.md §24 y §40.
   ========================================================================== */

(() => {
  if (window.nmaConsentimiento) return;

  const MARCA = '[data-nma-consentimiento]';

  /* Categorías de Shopify → qué permiso las habilita. `preferences` incluye
     personalización; `marketing` cubre chat de captación y publicidad. */
  const PERMISO = {
    analytics: (cp) => cp.analyticsProcessingAllowed(),
    marketing: (cp) => cp.marketingAllowed(),
    preferences: (cp) => cp.currentVisitorConsent().preferences === 'yes',
  };

  function promover(viejo) {
    const nuevo = document.createElement('script');
    for (const attr of Array.from(viejo.attributes)) {
      // El `type` se reemplaza y la marca propia no se copia: si el nodo nuevo
      // la llevara, una segunda pasada lo volvería a "promover" para siempre.
      if (attr.name === 'type' || attr.name === 'data-nma-consentimiento') continue;
      nuevo.setAttribute(attr.name, attr.value);
    }
    if (!viejo.src) nuevo.textContent = viejo.textContent;
    viejo.parentNode.replaceChild(nuevo, viejo);
    return nuevo;
  }

  function aplicar(cp) {
    let liberados = 0;
    for (const nodo of document.querySelectorAll('script[type="text/plain"]' + MARCA)) {
      const categoria = nodo.dataset.nmaConsentimiento;
      const prueba = PERMISO[categoria];
      // Categoría desconocida: se deja bloqueado. Ante la duda, no se carga.
      if (!prueba || !prueba(cp)) continue;
      promover(nodo);
      liberados++;
    }
    return liberados;
  }

  function arrancar(cp) {
    // 1 · el visitante puede llegar con la decisión ya tomada de antes
    aplicar(cp);

    // 2 · y puede tomarla ahora, sin recargar: es el criterio de R2
    document.addEventListener('visitorConsentCollected', () => aplicar(cp));
  }

  window.nmaConsentimiento = { aplicar: () => aplicar(window.Shopify.customerPrivacy) };

  function iniciar() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) {
          // Sin API de consentimiento no se libera nada. Es el lado seguro:
          // el visitante se queda sin chat, no sin privacidad.
          console.warn('[nma-consentimiento] no cargó la API de Shopify; nada se libera.');
          return;
        }
        arrancar(window.Shopify.customerPrivacy);
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
